import { Job, JobStatus, JobPriority } from "@/lib/schema";

// Transform Firebase Job to local display format
export type JobHubJob = Job & {
  applications: number;
  postedDate: string;
  deadline?: string;
};

// Transform Firebase job to JobHub format
export const transformJobForDisplay = (job: Job): JobHubJob => ({
  ...job,
  applications: job.referralCount || 0, // Use referralCount as applications
  postedDate: job.createdAt.toISOString().split('T')[0],
  deadline: job.updatedAt 
    ? new Date(job.updatedAt.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] 
    : undefined
});

// Color utilities for job status
export const getStatusColor = (status: JobStatus | string): string => {
  switch (status) {
    case JobStatus.ACTIVE:
    case 'active': 
      return 'bg-green-100 text-green-800';
    case JobStatus.DRAFT:
    case 'draft': 
      return 'bg-gray-100 text-gray-800';
    case JobStatus.PAUSED:
    case 'paused': 
      return 'bg-yellow-100 text-yellow-800';
    case JobStatus.CLOSED:
    case 'closed': 
      return 'bg-red-100 text-red-800';
    case JobStatus.FILLED:
    case 'filled': 
      return 'bg-blue-100 text-blue-800';
    case 'submitted': 
      return 'bg-yellow-100 text-yellow-800';
    case 'under_review': 
      return 'bg-blue-100 text-blue-800';
    case 'client_review': 
      return 'bg-purple-100 text-purple-800';
    case 'interview': 
      return 'bg-orange-100 text-orange-800';
    case 'hired': 
      return 'bg-green-100 text-green-800';
    case 'rejected': 
      return 'bg-red-100 text-red-800';
    default: 
      return 'bg-gray-100 text-gray-800';
  }
};

// Color utilities for job priority
export const getPriorityColor = (priority: JobPriority | string): string => {
  switch (priority) {
    case JobPriority.URGENT:
    case 'urgent': 
      return 'bg-red-100 text-red-800';
    case JobPriority.HIGH:
    case 'high': 
      return 'bg-orange-100 text-orange-800';
    case JobPriority.NORMAL:
    case 'normal': 
      return 'bg-blue-100 text-blue-800';
    case JobPriority.LOW:
    case 'low': 
      return 'bg-gray-100 text-gray-800';
    default: 
      return 'bg-gray-100 text-gray-800';
  }
};

// Priority icon utilities
export const getPriorityIcon = (priority: JobPriority | string) => {
  switch (priority) {
    case JobPriority.URGENT:
    case 'urgent': 
      return 'AlertCircle';
    case JobPriority.HIGH:
    case 'high': 
      return 'Star';
    default: 
      return null;
  }
};

// Filter jobs based on search and filters
export const filterJobs = (
  jobs: JobHubJob[], 
  searchTerm: string, 
  statusFilter: string, 
  priorityFilter: string
): JobHubJob[] => {
  return jobs.filter(job => {
    const matchesSearch = searchTerm === "" || 
                         job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (job as any).internalJobId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.skills.some(skill => skill.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = statusFilter === "all" || job.status === statusFilter;
    const matchesPriority = priorityFilter === "all" || job.priority === priorityFilter;
    
    return matchesSearch && matchesStatus && matchesPriority;
  });
};

// Calculate KPIs from jobs data
export const calculateJobKPIs = (jobs: JobHubJob[]) => ({
  activeJobs: jobs.filter(j => j.status === JobStatus.ACTIVE).length,
  draftJobs: jobs.filter(j => j.status === JobStatus.DRAFT).length,
  totalApplications: jobs.reduce((sum, job) => sum + job.applications, 0),
  highPriorityJobs: jobs.filter(j => j.priority === JobPriority.URGENT || j.priority === JobPriority.HIGH).length
});

// Create recruiter object from uid
export const createRecruiterObject = (
  uid: string, 
  recruiters?: Array<{id: string, firstName: string, lastName: string}>
) => {
  if (uid === "unassigned") return undefined;
  
  // If recruiters list is provided, try to find the recruiter by id (which is the Firebase UID)
  if (recruiters && recruiters.length > 0) {
    const recruiter = recruiters.find(r => r.id === uid);
    if (recruiter) {
      return { 
        id: recruiter.id, 
        name: `${recruiter.firstName} ${recruiter.lastName}` 
      };
    }
  }
  
  // If no recruiters list provided or not found, return undefined
  // This prevents storing corrupted data
  return undefined;
};

// Skill management utilities
export const addSkillToList = (skills: string[], newSkill: string): string[] => {
  const trimmedSkill = newSkill.trim();
  if (trimmedSkill && !skills.includes(trimmedSkill)) {
    return [...skills, trimmedSkill];
  }
  return skills;
};

export const removeSkillFromList = (skills: string[], skillToRemove: string): string[] => {
  return skills.filter(skill => skill !== skillToRemove);
};

// Bulk action types
export type BulkAction = 'activate' | 'pause' | 'delete';

export const getBulkActionLabel = (action: BulkAction): string => {
  switch (action) {
    case 'activate': return 'Activate';
    case 'pause': return 'Pause';
    case 'delete': return 'Delete';
    default: return action;
  }
};
